import { minutes, percent, type AssignmentId, type ISODateTime, type Minutes, type Percent, type TaskId } from '../domain/branded.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';

export interface AssignmentProgressUpdate {
  readonly assignmentId: AssignmentId;
  readonly actualWork: Minutes;
  readonly remainingWork: Minutes;
  readonly overtimeWork?: Minutes;
}

export interface TaskProgressUpdateInput {
  readonly taskId: TaskId;
  readonly assignments: readonly AssignmentProgressUpdate[];
  readonly actualStart: ISODateTime | null;
  readonly actualFinish: ISODateTime | null;
}

export type TaskProgressUpdateErrorCode =
  | 'TASK_NOT_FOUND'
  | 'SUMMARY_TASK'
  | 'ASSIGNMENT_SET_MISMATCH'
  | 'INVALID_WORK'
  | 'ACTUAL_START_REQUIRED'
  | 'ACTUAL_FINISH_REQUIRED'
  | 'ACTUAL_FINISH_NOT_ALLOWED'
  | 'INVALID_ACTUAL_DATES';

export interface TaskProgressUpdateError {
  readonly code: TaskProgressUpdateErrorCode;
  readonly message: string;
}

export type TaskProgressUpdateResult =
  | { readonly ok: true; readonly project: Project }
  | { readonly ok: false; readonly error: TaskProgressUpdateError };

const reject = (code: TaskProgressUpdateErrorCode, message: string): TaskProgressUpdateResult => ({ ok: false, error: { code, message } });

/**
 * Replaces progress for every assignment on one leaf task atomically. Planned
 * task/assignment dates are deliberately left unchanged; timephased values are
 * invalidated because their actual/remaining split is no longer authoritative.
 */
export function updateTaskProgress(project: Project, input: TaskProgressUpdateInput): TaskProgressUpdateResult {
  const task = project.tasks.find((candidate) => candidate.id === input.taskId);
  if (task === undefined) return reject('TASK_NOT_FOUND', `Task ${input.taskId as string} does not exist.`);
  if (task.isSummary || project.tasks.some((candidate) => candidate.parentId === task.id)) {
    return reject('SUMMARY_TASK', 'Progress must be updated on a leaf task.');
  }

  const existing = project.assignments.filter((assignment) => assignment.taskId === task.id);
  const existingIds = new Set(existing.map((assignment) => assignment.id as string));
  const submitted = new Map<string, AssignmentProgressUpdate>();
  for (const row of input.assignments) {
    const id = row.assignmentId as string;
    if (submitted.has(id)) return reject('ASSIGNMENT_SET_MISMATCH', `Assignment ${id} was supplied more than once.`);
    if (!existingIds.has(id)) return reject('ASSIGNMENT_SET_MISMATCH', `Assignment ${id} does not belong to task ${task.id as string}.`);
    submitted.set(id, row);
    const actual = row.actualWork as number;
    const remaining = row.remainingWork as number;
    const overtime = (row.overtimeWork ?? minutes(0)) as number;
    if (![actual, remaining, overtime].every((value) => Number.isFinite(value) && value >= 0) || overtime > actual) {
      return reject('INVALID_WORK', `Assignment ${id} has invalid actual, remaining, or overtime work.`);
    }
  }
  if (submitted.size !== existing.length) return reject('ASSIGNMENT_SET_MISMATCH', 'Every assignment on the task must be supplied exactly once.');

  let totalActual = 0;
  let totalRemaining = 0;
  for (const row of submitted.values()) {
    totalActual += row.actualWork as number;
    totalRemaining += row.remainingWork as number;
  }
  if (totalActual > 0 && input.actualStart === null) return reject('ACTUAL_START_REQUIRED', 'actualStart is required when actual work exists.');
  const completed = totalActual > 0 && totalRemaining === 0;
  if (completed && input.actualFinish === null) return reject('ACTUAL_FINISH_REQUIRED', 'actualFinish is required when all remaining work is zero.');
  if (!completed && input.actualFinish !== null) return reject('ACTUAL_FINISH_NOT_ALLOWED', 'actualFinish must be null until all remaining work is zero.');
  if (input.actualStart !== null && input.actualFinish !== null && input.actualFinish < input.actualStart) {
    return reject('INVALID_ACTUAL_DATES', 'actualFinish must not be earlier than actualStart.');
  }

  const totalWork = totalActual + totalRemaining;
  const ratio = totalWork === 0 ? 0 : (totalActual / totalWork) * 100;
  const assignments = project.assignments.map((assignment) => {
    if (assignment.taskId !== task.id) return assignment;
    const row = submitted.get(assignment.id as string)!;
    return {
      ...assignment,
      work: minutes((row.actualWork as number) + (row.remainingWork as number)),
      actualWork: row.actualWork,
      remainingWork: row.remainingWork,
      overtimeWork: row.overtimeWork ?? minutes(0),
      timephased: null,
    };
  });
  const tasks = project.tasks.map((candidate) => candidate.id === task.id ? {
    ...candidate,
    work: minutes(totalWork),
    percentWorkComplete: percent(ratio),
    percentComplete: percent(ratio),
    actualStart: totalActual > 0 ? input.actualStart : null,
    actualFinish: completed ? input.actualFinish : null,
  } : candidate);
  return { ok: true, project: { ...project, tasks, assignments } };
}

// ---------------------------------------------------------------------------
// 摘要任務的進度彙總
// ---------------------------------------------------------------------------

/**
 * 摘要任務的進度：以子任務工時加權；工時全為 0 時退回以工期加權，
 * 兩者皆為 0 時退回簡單平均。`applySchedule()` 與進度回報流程共用這一份規則，
 * 避免「排程後」與「回報後」的摘要百分比互相矛盾。
 */
export function rollUpSummaryPercent(
  children: readonly Task[],
  select: (task: Task) => Percent,
): number {
  let totalWork = 0;
  let weightedWork = 0;
  let totalDuration = 0;
  let weightedDuration = 0;
  let sum = 0;
  for (const child of children) {
    const childWork = child.work as number;
    const childDuration = child.durationMinutes as number;
    const childPercent = select(child) as number;
    totalWork += childWork;
    weightedWork += childWork * childPercent;
    totalDuration += childDuration;
    weightedDuration += childDuration * childPercent;
    sum += childPercent;
  }
  if (totalWork > 0) return weightedWork / totalWork;
  if (totalDuration > 0) return weightedDuration / totalDuration;
  return children.length > 0 ? sum / children.length : 0;
}

/**
 * 由葉節點往上重算每個摘要任務的 `percentComplete` / `percentWorkComplete`。
 * 進度是儲存值，改了葉節點若不往上收斂，表格與看板會看到 0% 的摘要。
 */
export function rollUpSummaryProgress(project: Project): Project {
  const childrenByParent = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const bucket = childrenByParent.get(task.parentId as string);
    if (bucket === undefined) childrenByParent.set(task.parentId as string, [task]);
    else bucket.push(task);
  }
  if (childrenByParent.size === 0) return project;

  const resolved = new Map<string, Task>();
  const visiting = new Set<string>();
  let changed = false;

  const resolve = (task: Task): Task => {
    const key = task.id as string;
    const cached = resolved.get(key);
    if (cached !== undefined) return cached;
    // 階層成環時（schedule() 會拒絕這種輸入）僅止住遞迴。
    if (visiting.has(key)) return task;
    visiting.add(key);

    const children = childrenByParent.get(key);
    let next = task;
    if (children !== undefined && children.length > 0) {
      const resolvedChildren = children.map((child) => resolve(child));
      const percentComplete = percent(rollUpSummaryPercent(resolvedChildren, (child) => child.percentComplete));
      const percentWorkComplete = percent(rollUpSummaryPercent(resolvedChildren, (child) => child.percentWorkComplete));
      if (percentComplete !== task.percentComplete || percentWorkComplete !== task.percentWorkComplete) {
        next = { ...task, percentComplete, percentWorkComplete };
        changed = true;
      }
    }
    visiting.delete(key);
    resolved.set(key, next);
    return next;
  };

  const tasks = project.tasks.map((task) => resolve(task));
  return changed ? { ...project, tasks } : project;
}
